import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Kanban, RefreshCw } from 'lucide-react';
import toast from 'react-hot-toast';
import axios from 'axios';
import { api } from '../services/api';
import type { OrdemServico, OsKanbanNavigationState } from '../types/ordemServico';
import {
  agruparOsPorColuna,
  explainKanbanPlacement,
  getFaseAtivaColuna,
  getOsKey,
  KANBAN_COLUMNS,
  type KanbanColumnId
} from '../utils/osKanban';
import {
  formatarTempoRestante,
  getFaseAlertaStatus,
  getFaseIcon,
  getOSAlertas,
  getStatusColor
} from '../utils/osAlertas';
import { collectKanbanScrollPositions, restoreKanbanScrollPositions, saveKanbanRestoreState } from '../utils/kanbanNavigation';
import EtiquetasModal from '../components/EtiquetasModal';

const OsKanban: React.FC = () => {
  const navigate = useNavigate();
  const [ordens, setOrdens] = useState<OrdemServico[]>([]);
  const [loading, setLoading] = useState(true);
  const [isFetching, setIsFetching] = useState(false);
  const [etiquetaOS, setEtiquetaOS] = useState<OrdemServico | null>(null);
  const requestIdRef = useRef(0);
  const hasLoadedRef = useRef(false);
  const abortRef = useRef<AbortController | null>(null);
  const boardRef = useRef<HTMLDivElement | null>(null);

  const fetchOrdens = async (options?: { silent?: boolean }) => {
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    const requestId = ++requestIdRef.current;

    try {
      setIsFetching(true);
      if (!hasLoadedRef.current && !options?.silent) {
        setLoading(true);
      }

      const response = await api.get('/os', {
        signal: controller.signal,
        timeout: 90000
      });

      if (requestId !== requestIdRef.current) return;

      const ordensData = response.data?.os || response.data?.data || response.data || [];
      setOrdens(Array.isArray(ordensData) ? ordensData : []);
      hasLoadedRef.current = true;
    } catch (error: any) {
      if (axios.isCancel(error) || error?.code === 'ERR_CANCELED') return;
      if (requestId !== requestIdRef.current) return;

      console.error('Erro ao carregar OSs para kanban:', error);

      if (error.response?.status === 429) {
        toast.error('Muitas requisições. Aguarde um momento...');
      } else if (!hasLoadedRef.current) {
        toast.error('Erro ao carregar ordens de serviço');
        setOrdens([]);
      } else {
        toast.error('Erro ao atualizar. Mantendo os dados anteriores.');
      }
    } finally {
      if (requestId === requestIdRef.current) {
        setLoading(false);
        setIsFetching(false);
      }
    }
  };

  useEffect(() => {
    fetchOrdens();
    const intervalId = setInterval(() => fetchOrdens({ silent: true }), 2 * 60 * 1000);
    return () => {
      clearInterval(intervalId);
      abortRef.current?.abort();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (loading || !boardRef.current) return;

    requestAnimationFrame(() => {
      if (boardRef.current) {
        restoreKanbanScrollPositions(boardRef.current);
      }
    });
  }, [loading, ordens]);

  const grupos = agruparOsPorColuna(ordens);

  const irParaOrdensServico = (state: OsKanbanNavigationState, os: OrdemServico) => {
    if (boardRef.current) {
      saveKanbanRestoreState(
        collectKanbanScrollPositions(boardRef.current, getOsKey(os))
      );
    }
    navigate('/ordens-servico', { state: { ...state, fromKanban: true, returnToKanban: true } });
  };

  const renderCard = (os: OrdemServico, columnId: KanbanColumnId) => {
    const isSisfuner = os.isSisfuner === true;
    const placement = explainKanbanPlacement(os);
    const { temAlerta, temAtrasada } = !isSisfuner ? getOSAlertas(os) : { temAlerta: false, temAtrasada: false };
    const faseColuna = getFaseAtivaColuna(os, columnId);
    const alertaFaseColuna = faseColuna ? getFaseAlertaStatus(faseColuna) : null;

    return (
      <div
        key={getOsKey(os)}
        data-kanban-os-key={getOsKey(os)}
        title={placement.motivo}
        className={`rounded-md border bg-white p-2 shadow-sm ${
          temAtrasada
            ? 'border-l-4 border-red-500'
            : temAlerta
            ? 'border-l-4 border-orange-400'
            : 'border-gray-200'
        }`}
      >
        <div className="mb-2">
          <h3 className="text-sm font-semibold text-gray-900 leading-tight line-clamp-2">{os.nome_falecido}</h3>
          <p className="text-[11px] text-gray-500 mt-0.5 truncate">
            {isSisfuner
              ? `N.C. ${os._DADOSOS_NumOrdem || os.numero_nota_contratacao}`
              : os.numero_nota_contratacao || os.codigo_acesso}
          </p>
        </div>

        {!isSisfuner && (
          <>
            <div className="flex items-center gap-1 mb-2 flex-wrap">
              <span className={`inline-flex px-2 py-0.5 text-[10px] font-semibold rounded-full ${getStatusColor(os.status_geral)}`}>
                {os.status_geral}
              </span>
              {faseColuna && (
                <span className={`inline-flex px-2 py-0.5 text-[10px] rounded ${getStatusColor(faseColuna.status_fase)}`}>
                  {getFaseIcon(faseColuna.nome_fase)} {faseColuna.status_fase}
                </span>
              )}
              {alertaFaseColuna && (
                <span
                  className={`inline-flex px-1.5 py-0.5 text-[10px] font-bold rounded ${
                    alertaFaseColuna === 'atrasada' ? 'bg-red-500 text-white' : 'bg-orange-400 text-white'
                  }`}
                >
                  ⚠️ {faseColuna ? formatarTempoRestante(faseColuna) : ''}
                </span>
              )}
            </div>

            {os.fases && os.fases.length > 0 && (
              <div className="text-[11px] mb-2 space-y-0.5">
                {[...os.fases]
                  .sort((a, b) => a.ordem - b.ordem)
                  .map((fase) => (
                    <div
                      key={fase.id ?? fase.nome_fase}
                      className={fase.motorista ? 'text-blue-700' : 'text-gray-500'}
                    >
                      {getFaseIcon(fase.nome_fase)} {fase.nome_fase}:{' '}
                      {fase.motorista
                        ? `${fase.motorista.nome}${fase.veiculo?.placa ? ` · ${fase.veiculo.placa}` : ''}`
                        : 'Sem motorista'}
                    </div>
                  ))}
              </div>
            )}
          </>
        )}

        <div className="flex flex-wrap gap-1 pt-2 border-t border-gray-100">
          {isSisfuner ? (
            <button
              type="button"
              onClick={() =>
                irParaOrdensServico(
                  {
                    action: 'monitor',
                    numOrdemSisfuner: os._DADOSOS_NumOrdem
                  },
                  os
                )
              }
              className="w-full text-[11px] bg-blue-600 text-white px-2 py-1 rounded hover:bg-blue-700"
            >
              Iniciar
            </button>
          ) : (
            <>
              <button
                type="button"
                onClick={() => irParaOrdensServico({ action: 'view', osId: os.id! }, os)}
                className="flex-1 text-[11px] bg-blue-50 text-blue-700 px-1.5 py-1 rounded hover:bg-blue-100"
              >
                Ver
              </button>
              <button
                type="button"
                onClick={() => irParaOrdensServico({ action: 'edit', osId: os.id! }, os)}
                className="flex-1 text-[11px] bg-gray-100 text-gray-700 px-1.5 py-1 rounded hover:bg-gray-200"
              >
                Editar
              </button>
              <button
                type="button"
                onClick={() => irParaOrdensServico({ action: 'assign', osId: os.id! }, os)}
                className="flex-1 text-[11px] bg-green-50 text-green-700 px-1.5 py-1 rounded hover:bg-green-100"
              >
                Mot.
              </button>
              <button
                type="button"
                onClick={() => setEtiquetaOS(os)}
                className="flex-1 text-[11px] bg-amber-50 text-amber-800 px-1.5 py-1 rounded hover:bg-amber-100"
                title="Etiquetas"
              >
                Etiquetas
              </button>
            </>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="px-3 lg:px-4 py-3 flex flex-col h-[calc(100vh-4.5rem)]">
      <div className="flex flex-row items-center justify-between gap-3 mb-3 flex-shrink-0">
        <div className="min-w-0">
          <h1 className="text-xl font-semibold text-gray-900 flex items-center gap-2">
            <Kanban className="h-6 w-6 text-blue-600 flex-shrink-0" />
            <span className="truncate">OSs Kanban</span>
          </h1>
          <p className="text-xs text-gray-500 mt-0.5 hidden sm:block">
            Fluxo por fase · card sai do quadro só após conclusão do sepultamento
          </p>
        </div>
        <button
          type="button"
          onClick={() => fetchOrdens({ silent: true })}
          className="inline-flex items-center gap-2 px-3 py-1.5 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 flex-shrink-0"
        >
          <RefreshCw className={`h-4 w-4 ${loading || isFetching ? 'animate-spin' : ''}`} />
          Atualizar
        </button>
      </div>

      {loading ? (
        <div className="flex-1 flex items-center justify-center text-gray-500">Carregando quadro...</div>
      ) : (
        <div ref={boardRef} className="flex-1 min-h-0 grid grid-cols-6 gap-2">
          {KANBAN_COLUMNS.map((col) => (
            <div
              key={col.id}
              className="flex flex-col min-w-0 min-h-0"
            >
              <div className={`rounded-t-md border px-2 py-1.5 flex-shrink-0 ${col.headerClass}`}>
                <div className="font-semibold text-xs leading-tight">{col.title}</div>
                <div className="text-[10px] opacity-75 leading-tight line-clamp-2">{col.subtitle}</div>
                <div className="text-[11px] font-bold mt-0.5">{grupos[col.id].length}</div>
              </div>
              <div
                data-kanban-column={col.id}
                className="flex-1 min-h-0 overflow-y-auto border border-t-0 border-gray-200 rounded-b-md bg-gray-50 p-1.5 space-y-1.5"
              >
                {grupos[col.id].length === 0 ? (
                  <p className="text-[10px] text-gray-400 text-center py-4">—</p>
                ) : (
                  grupos[col.id].map((os) => renderCard(os, col.id))
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {etiquetaOS && (
        <EtiquetasModal os={etiquetaOS} onClose={() => setEtiquetaOS(null)} />
      )}
    </div>
  );
};

export default OsKanban;
